import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import {
  InspectionDetails,
  InspectionPhoto,
  InspectionsDashboardTabs,
  InspectionStatusReversed,
  InspectionTask,
} from '../types/types';
import { InspectionService } from '../services/inspectionService';
import { InspectionPhotosStore } from './inspectionPhotosStore';
import { PhotoResultStatus, VerificationCheck } from '../types/types';
import { GalleryStore } from '../gallery/stores/galleryStore';

export class InspectionStore {
  private globalStore: GlobalStore;
  inspectionPhotosStore: InspectionPhotosStore;
  galleryStore: GalleryStore;
  inspectionService: InspectionService;
  inspectionsDashboardDrawsGridStore: GridStore;
  inspectionsDashboardInternalValuationsGridStore: GridStore;
  inspectionsDashboardAuctionPropertiesGridStore: GridStore;
  tab: InspectionsDashboardTabs;
  inspectionId: number;
  inspectionDetails: InspectionDetails;
  inspectionType: string
  inspectionTasks: InspectionTask[];
  inspectionPhotoSelected: InspectionPhoto;
  isLoading: boolean;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.tab = InspectionsDashboardTabs.INSPECTIONS_DRAWS;
    this.inspectionPhotosStore = new InspectionPhotosStore(this.globalStore);
    this.galleryStore = new GalleryStore(this.globalStore);
    this.inspectionService = new InspectionService();
    this.inspectionsDashboardDrawsGridStore = new GridStore(
      () => this.fetchInspectionsDashboardDraws(),
      null,
      15
    );
    this.inspectionsDashboardInternalValuationsGridStore = new GridStore(
      () => this.fetchInspectionsDashboardDraws(),
      null,
      15
    );

    makeObservable(this, {
      tab: observable,
      reset: action,
      inspectionType: observable,
      inspectionId: observable,
      inspectionDetails: observable,
      setInspectionType: action,
      inspectionTasks: observable,
      inspectionPhotoSelected: observable,
      loadInspectionByExternalIds: flow,
      loadInspection: flow,
      setInspectionPhotoSelected: flow,
      isLoading: observable,
      canApproveManually: computed
    });

    this.reset();
  }

  *loadInspectionByExternalIds(externalId1: number, externalId2: number) {

    try {
      const response = yield this.inspectionService.getInspectionDetailsByExternalIds(
        externalId1,
        externalId2
      );
      if (
        response?.data?.status === 'OK' &&
        !!response?.data?.data.inspection
      ) {
        this.inspectionDetails = response?.data?.data.inspection;
        this.inspectionTasks = response?.data?.data.tasks;
        this.inspectionId = this.inspectionDetails.inspectionId;

        this.isLoading = true;
        yield this.inspectionPhotosStore.loadInspectionPhotos(
          this.inspectionDetails
        );
        this.isLoading = false;
      } else {
        throw new Error(
          'There was an error while retrieving the inspection data.'
        );
      }
    } catch (err) {
      this.isLoading = false;
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving the inspection data.',
      });
    }
  }

  *loadInspection(inspectionId: number) {
    this.inspectionId = inspectionId;

    try {
      const response = yield this.inspectionService.getInspectionDetails(
        inspectionId
      );
      if (
        response?.data?.status === 'OK' &&
        !!response?.data?.data.inspection
      ) {
        this.inspectionDetails = response?.data?.data.inspection;
        this.inspectionTasks = response?.data?.data.tasks;

        this.isLoading = true;
        yield this.inspectionPhotosStore.loadInspectionPhotos(
          this.inspectionDetails
        );
        this.isLoading = false;
      } else {
        throw new Error(
          'There was an error while retrieving the inspection data.'
        );
      }
    } catch (err) {
      this.isLoading = false;
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving the inspection data.',
      });
    }
  }

  *setInspectionPhotoSelected(inspectionPhotoSelected: InspectionPhoto) {
    this.inspectionPhotoSelected = inspectionPhotoSelected;
    yield this.inspectionPhotosStore.fetchPhoto(inspectionPhotoSelected.inspectionPhotoId);
  }

  setInspectionType(inspectionType: string) {
    this.inspectionType = inspectionType;
  }

  reset(exceptGrid = false) {
    this.inspectionId = null;
    this.inspectionDetails = null;
    this.inspectionPhotoSelected = null;
    this.isLoading = false;
    this.inspectionPhotosStore.reset();

    if (!exceptGrid) {
      this.tab = InspectionsDashboardTabs.INSPECTIONS_DRAWS;
      this.inspectionsDashboardDrawsGridStore.reset();
    }
  }

  checkResult(
    inspectionPhoto: InspectionPhoto,
    verificationCheck: typeof VerificationCheck[keyof typeof VerificationCheck]
  ) {
    switch (verificationCheck) {
      case VerificationCheck.DATA_INTEGRITY:
        return PhotoResultStatus.NA;
      case VerificationCheck.CAPTURE_INTEGRITY:
        return inspectionPhoto.isSignatureValid
          ? PhotoResultStatus.OK
          : PhotoResultStatus.HAS_ISSUES;
      case VerificationCheck.TIME_DATE:
        return inspectionPhoto.isHashValid
          ? PhotoResultStatus.OK
          : PhotoResultStatus.HAS_ISSUES;
      case VerificationCheck.LOCATION:
        return inspectionPhoto.isLocationValid
          ? PhotoResultStatus.OK
          : inspectionPhoto.isLocationValidManualOverride
          ? PhotoResultStatus.OVERRIDDEN
          : PhotoResultStatus.HAS_ISSUES;
      case VerificationCheck.IMAGE_ANALYSIS:
        return PhotoResultStatus.NA;
      case VerificationCheck.REVERSE_IMAGE_SEARCH:
        return PhotoResultStatus.NA;
      default:
        return PhotoResultStatus.NA;
    }
  }

  setTab(tab: InspectionsDashboardTabs) {
    this.tab = tab;
  }

  getCurrentGridStore(): GridStore {
    switch (this.tab) {
      case InspectionsDashboardTabs.INSPECTIONS_DRAWS:
        this.setInspectionType('DRAWS')
        return this.inspectionsDashboardDrawsGridStore;
      case InspectionsDashboardTabs.INSPECTIONS_INTERNAL_VALUATIONS:
        this.setInspectionType('INTERNAL_VALUATION')
        return this.inspectionsDashboardInternalValuationsGridStore;
      case InspectionsDashboardTabs.INSPECTIONS_AUCTION_PROPERTIES:
        return this.inspectionsDashboardAuctionPropertiesGridStore;
      default:
        return this.inspectionsDashboardDrawsGridStore;
    }
  }

  private fetchInspectionsDashboardDraws(): Promise<ApiResponse> {
    try {
      const gridStore = this.getCurrentGridStore();
      const {
        dropdownFilters,
        filters,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
      } = gridStore.gridData.meta;

      let updatedFilters = filters
      if (this.inspectionType) {
        updatedFilters = {
          ...filters,
          inspectionType: this.inspectionType
        }
      }

      return this.inspectionService.getAllInspections(
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        updatedFilters,
        dropdownFilters,
      );
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching inspections draws dashboard data',
      });
    }
  }

  get canApproveManually(){
    return this.globalStore.isInternalPortal;
  }
}
